// /group/:code — public study-group join page. Non-logged-in visitors see the
// group name + seats left and are pushed to /register?group=CODE which the
// AuthPage should pick up and pass through on successful signup.
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Users, Sparkles, Check, Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { previewStudyGroup, joinStudyGroup } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";

export default function StudyGroupJoinPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState(null);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(null);

  useEffect(() => {
    previewStudyGroup(code)
      .then(setPreview)
      .catch((e) => setErr(e?.response?.data?.detail || "Group not found."));
  }, [code]);

  const doJoin = async () => {
    if (!user) {
      // Push to signup with the group code so we can auto-join post-signup.
      navigate(`/register?group=${encodeURIComponent(code)}`);
      return;
    }
    setJoining(true);
    try {
      const r = await joinStudyGroup(code);
      setJoined(r);
      if (r.trial?.granted) {
        toast.success(`Welcome to "${r.name}" — you have ${r.trial.days} days of Pro on the house.`);
      } else if (r.already_member) {
        toast.message(`You're already in "${r.name}".`);
      } else {
        toast.message(`Joined "${r.name}".`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't join the group.");
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-background text-foreground p-4">
      <SEO
        title={preview ? `Join "${preview.name}" on Lectura — free study group` : "Join a Lectura study group"}
        description="Join a Lectura study group with your friend's invite code. First 8 members get 14 days of Pro free, no credit card required."
      />
      <div className="w-full max-w-md rounded-3xl border border-border bg-card shadow-lg p-8 text-center">
        {err ? (
          <>
            <span className="inline-grid place-items-center h-14 w-14 rounded-2xl bg-destructive/10 text-destructive">
              <Users className="h-7 w-7" />
            </span>
            <h1 className="mt-4 font-serif text-2xl font-bold">Group not found</h1>
            <p className="mt-2 text-sm text-muted-foreground">{err}</p>
            <Link to="/" className="mt-6 inline-flex rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-bold">
              Back to Lectura
            </Link>
          </>
        ) : !preview ? (
          <div className="py-10">
            <Loader2 className="h-8 w-8 animate-spin text-accent mx-auto" />
          </div>
        ) : joined ? (
          <>
            <span className="inline-grid place-items-center h-14 w-14 rounded-2xl bg-emerald-100 text-emerald-700">
              <Check className="h-7 w-7" />
            </span>
            <h1 className="mt-4 font-serif text-2xl font-bold">You're in.</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Welcome to "{joined.name}". {joined.trial?.granted
                ? `Your ${joined.trial.days}-day Pro trial starts now.`
                : "Your account already has Pro, so no trial was applied."}
            </p>
            <button
              data-testid="study-group-continue"
              onClick={() => navigate("/")}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-bold hover:opacity-90"
            >
              Start studying <ArrowRight className="h-4 w-4" />
            </button>
          </>
        ) : (
          <>
            <div className="inline-flex items-center gap-2 rounded-full bg-accent/15 text-accent px-3 py-1 text-xs font-bold uppercase tracking-widest">
              <Sparkles className="h-3.5 w-3.5" /> Study group invite
            </div>
            <h1 className="mt-3 font-serif text-3xl font-bold">"{preview.name}"</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {preview.creator_name} invited you. {preview.seats_left > 0 ? (
                <>Grab one of <span className="font-semibold text-foreground">{preview.seats_left}</span> seats left.</>
              ) : (
                <>This group is full.</>
              )}
            </p>

            <div className="mt-6 rounded-2xl border-2 border-accent/40 bg-accent/5 p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-accent">Your seat comes with</p>
              <p className="mt-1 font-serif text-2xl font-bold">{preview.trial_days} days of Pro</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Unlimited lectures, AI narration, Ask the Professor, Focus Mode. No credit card.
              </p>
            </div>

            <button
              data-testid="study-group-join-btn"
              onClick={doJoin}
              disabled={joining || preview.seats_left === 0}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-3 text-sm font-bold hover:opacity-90 disabled:opacity-50"
            >
              {joining ? <Loader2 className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}
              {user ? "Join the group" : "Sign up & join"}
            </button>

            <div className="mt-4 flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5" /> FERPA-Ready · COPPA-Compliant · No card required
            </div>
          </>
        )}
      </div>
    </div>
  );
}
